import { STATES } from 'src/constants/states';
import { PaymentFormValues } from 'src/pages/payment-msi/PaymentMsiV2/PaymentMsiV2';
import { Except } from 'src/types';

export interface ValidatePaymentFormError {
  fieldName: string;
  message: string;
}

export interface ValidatePaymentFormResult {
  isValid: boolean;
  errors: ValidatePaymentFormError[];
}

type ValidatePaymentFormValues = Except<
  PaymentFormValues,
  'interestedParty'
> & {
  interestedParty: PaymentFormValues['interestedParty'] | null;
};

export function validatePaymentForm(formData: ValidatePaymentFormValues) {
  const formErrors: ValidatePaymentFormError[] = [];

  if (!formData.paymentPlan) {
    formErrors.push({
      fieldName: 'paymentPlan',
      message: 'Please select a payment plan',
    });
  }

  const interestedPartyErrors = validateInterestedParty(
    formData.interestedParty,
  );
  formErrors.push(...interestedPartyErrors);

  const additionalInsuredErrors = validateAdditionalInsured(
    formData.additionalInsured,
  );
  formErrors.push(...additionalInsuredErrors);

  if (!formData.paymentData) {
    formErrors.push({
      fieldName: 'paymentData',
      message: 'Please enter payment data',
    });
  }

  const billingAddressErrors = validateBillingAddress(formData.billingAddress);
  formErrors.push(...billingAddressErrors);

  const isValid = formErrors.length === 0;

  return {
    isValid,
    errors: formErrors,
  };
}

export function validateInterestedParty(
  interestedParty: ValidatePaymentFormValues['interestedParty'],
): ValidatePaymentFormError[] {
  const interestedPartyErrors: ValidatePaymentFormError[] = [];

  if (!interestedParty) {
    return interestedPartyErrors;
  }

  const isEmailValid = isValidEmail(interestedParty.email);
  if (!isEmailValid) {
    interestedPartyErrors.push({
      fieldName: 'interestedParty.email',
      message: 'Invalid email address',
    });
  }

  if (!interestedParty.fullName) {
    interestedPartyErrors.push({
      fieldName: 'interestedParty.fullName',
      message: 'Please enter valid interested party name',
    });
  }

  return interestedPartyErrors;
}

export function validateAdditionalInsured(
  additionalInsured: PaymentFormValues['additionalInsured'],
) {
  if (additionalInsured.length === 0) {
    return [];
  }

  const additionalInsuredErrors: ValidatePaymentFormError[] = [];

  additionalInsured.forEach((insured, index) => {
    if (!insured.firstName) {
      additionalInsuredErrors.push({
        fieldName: `additionalInsured.${index}.firstName`,
        message: 'Please enter a valid name for additional insured',
      });
    }

    if (!insured.lastName) {
      additionalInsuredErrors.push({
        fieldName: `additionalInsured.${index}.lastName`,
        message: 'Please enter a valid name for additional insured',
      });
    }

    const isEmailValid = isValidEmail(insured.email);

    if (!isEmailValid) {
      additionalInsuredErrors.push({
        fieldName: `additionalInsured.${index}.email`,
        message: 'Invalid email address',
      });
    }
  });

  return additionalInsuredErrors;
}

function validateBillingAddress(
  billingAddress: PaymentFormValues['billingAddress'],
): ValidatePaymentFormError[] {
  const billingAddressErrors: ValidatePaymentFormError[] = [];

  if (!billingAddress) {
    billingAddressErrors.push({
      fieldName: 'billingAddress',
      message: 'Please enter billing address',
    });
  }

  if (!billingAddress.addressLine1) {
    billingAddressErrors.push({
      fieldName: 'billingAddress.addressLine1',
      message: 'Please enter address line 1',
    });
  }

  if (!billingAddress.city) {
    billingAddressErrors.push({
      fieldName: 'billingAddress.city',
      message: 'Please enter city',
    });
  }

  const isStateValid = Boolean(
    STATES.find((state) => state.value === billingAddress.state),
  );

  if (!billingAddress.state || !isStateValid) {
    billingAddressErrors.push({
      fieldName: 'billingAddress.state',
      message: 'Please enter state',
    });
  }

  if (!billingAddress.zipCode) {
    billingAddressErrors.push({
      fieldName: 'billingAddress.zipCode',
      message: 'Please enter zip code',
    });
  }

  return billingAddressErrors;
}

function isValidEmail(email: string) {
  if (!email) {
    return false;
  }

  return email.includes('@') && email.includes('.');
}
